import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { NotificationToaster } from 'components/notifications';
import { isValidEmailAddress } from 'hooks/useValidation';

const dateApiFormat = "YYYY-MM-DD";
const ohReferralMaxLength = 50;
const badRequestCode = 400;

// The employee sign-off methods a review type offers are filtered per review type, so a method is
// identified by the key the API returns alongside its id rather than by the id itself.
export const onScreenSignOffMethodKey = "ONSCREEN";
export const emailSignOffMethodKey = "EMAIL";

// The header status text reads identically to the listing the review was reached from, so it
// resolves the bands from the shared vocabulary rather than holding its own copy.
export { pendingStatusBands as statusBands } from 'pages/employee-reviews/shared';

// Date-only fields (the employee's profile expiry dates, the medical dates, and a
// custom next review date) are sent to the API as yyyy-MM-dd strings, matching the
// convention already used for other DateTime? fields in this codebase (see
// ReviewTypeDetailsStep).
export function toApiDate(value) {
    if (value == null || value === "") {
        return null;
    }

    return moment(value).format(dateApiFormat);
}

// The API serialises the medical dates with a time component, so they are normalised to
// the format the date fields write back - otherwise re-picking the date already shown
// would mark the step dirty.
export function toMedicalFormModel(medicalInformation) {
    return {
        lastHealthAssessmentDate: toApiDate(medicalInformation?.lastHealthAssessmentDate),
        lastEyesightAssessmentDate: toApiDate(medicalInformation?.lastEyesightAssessmentDate),
        reportableMedicalCondition: medicalInformation?.reportableMedicalCondition ?? false,
        ohReferral: medicalInformation?.ohReferral ?? "",
        ohReferralDate: toApiDate(medicalInformation?.ohReferralDate)
    };
}

// The medical endpoint replaces all five values, so an empty referral is sent as null
// to clear it.
export function buildMedicalInformationRequest(draft) {
    return {
        lastHealthAssessmentDate: draft?.lastHealthAssessmentDate ?? null,
        lastEyesightAssessmentDate: draft?.lastEyesightAssessmentDate ?? null,
        reportableMedicalCondition: draft?.reportableMedicalCondition ?? false,
        ohReferral: draft?.ohReferral ? draft.ohReferral : null,
        ohReferralDate: draft?.ohReferralDate ?? null
    };
}

function isFutureDate(value) {
    return value != null && moment(value, dateApiFormat).isAfter(moment(), "day");
}

// Mirrors the rules the medical endpoint enforces - the referral length and the
// occupational-health cross-field rule from EmployeeMedicalInformationRequest, and the
// past-date rule from EmployeeService.RejectFutureAssessmentDates - so the manager is told
// what to correct inline rather than through a 400. Messages match the server and the
// profile's Medical tab so the same rule never reads differently on a different screen.
// Returned as useValidation external rules; the hook keeps only the first message per
// field, so the length error takes precedence over the cross-field one on the referral.
export function buildMedicalValidationRules(draft) {
    const ohReferral = draft?.ohReferral ?? "";
    const ohReferralDate = draft?.ohReferralDate ?? null;
    const occupationalHealthWithoutCondition = !(draft?.reportableMedicalCondition ?? false)
        && (ohReferral !== "" || ohReferralDate != null);
    const occupationalHealthMessage = "The occupational health referral and referral date can only be set when a reportable medical condition is recorded. Clear them, or record a reportable medical condition.";

    return [
        {
            fieldName: "ohReferral",
            valid: ohReferral.length <= ohReferralMaxLength,
            errorMessage: `The occupational health referral cannot be longer than ${ohReferralMaxLength} characters.`
        },
        {
            fieldName: "lastHealthAssessmentDate",
            valid: !isFutureDate(draft?.lastHealthAssessmentDate),
            errorMessage: "The last health assessment date must be a date in the past."
        },
        {
            fieldName: "lastEyesightAssessmentDate",
            valid: !isFutureDate(draft?.lastEyesightAssessmentDate),
            errorMessage: "The last eyesight assessment date must be a date in the past."
        },
        {
            // Reported against the first populated field so the message appears once,
            // beside a value the manager has to act on.
            fieldName: ohReferral !== "" ? "ohReferral" : "ohReferralDate",
            valid: !occupationalHealthWithoutCondition,
            errorMessage: occupationalHealthMessage
        }
    ];
}

// A rejected save carries its validation message when the endpoint rejected the values, so that
// message is shown rather than a generic failure. The employee endpoints reject with the raw axios
// error and the medical endpoint with its response, so the status and body are read from either.
export function showSaveFailureToast(error, genericMessage) {
    if ((error?.response?.status ?? error?.status) === badRequestCode) {
        NotificationToaster.show(Intent.WARNING, error?.response?.data ?? error?.data);
    } else {
        NotificationToaster.show(Intent.DANGER, genericMessage);
    }
}

// The Outcome step cannot be passed without the outcome level a review type makes mandatory.
export function buildOutcomeGating(pendingReview, wizardAnswers) {
    const outcomeLevels = pendingReview?.outcomeLevels;
    const canProceed = !(outcomeLevels?.active && outcomeLevels?.mandatory) || wizardAnswers.outcomeLevelNumber != null;

    return {
        canProceed,
        cantProceedTooltip: canProceed ? null : "An outcome level must be selected before continuing."
    };
}

// Resolves the key of the employee sign-off method the manager has chosen, so the values that
// method calls for can be asked for and submitted without any screen holding the method ids.
export function resolveEmployeeSignOffMethodKey(pendingReview, employeeSignOffMethodId) {
    const method = (pendingReview?.signOff?.employeeMethods ?? [])
        .find((employeeMethod) => employeeMethod.id === employeeSignOffMethodId);

    return method?.key ?? null;
}

// The review cannot be completed until it has been signed off, so the tooltip lists whatever is
// still outstanding.
export function buildSignOffGating(pendingReview, wizardAnswers) {
    const missing = [];
    const methodKey = resolveEmployeeSignOffMethodKey(pendingReview, wizardAnswers.employeeSignOffMethodId);

    if (wizardAnswers.employeeSignOffMethodId == null) {
        missing.push("select a sign-off method");
    }

    if (pendingReview?.signOff?.requireManagerSignature && !wizardAnswers.managerSignature) {
        missing.push("draw the manager signature");
    }

    if (methodKey === onScreenSignOffMethodKey && !wizardAnswers.employeeSignature) {
        missing.push("draw the employee signature");
    }

    if (methodKey === emailSignOffMethodKey && !isValidEmailAddress((wizardAnswers.signOffEmailAddress ?? "").trim())) {
        missing.push("enter a valid driver email address");
    }

    if (!wizardAnswers.confirmChecked) {
        missing.push("confirm the review has been completed");
    }

    const canProceed = missing.length === 0;

    return {
        canProceed,
        cantProceedTooltip: canProceed ? null : `Please ${missing.join(", ")} before completing.`
    };
}

// Builds the POST body for completing the pending review. The review's full topic set
// must be present, so every topic the pending review lists is submitted: a topic with a
// comment in topicComments (one the wizard rendered a step for) carries that comment and
// is marked reviewed, and any other topic is submitted with a blank comment and
// reviewed: false. Only the topic key, comment and reviewed flag are sent - a topic's own
// values are persisted through its own endpoint and the completed review's snapshot is
// rebuilt from the stored record.
export function buildCompleteReviewRequest(
    {
        pendingReview,
        topicComments = {},
        outcomeLevelNumber,
        outcomeSummary,
        customNextReviewDate,
        employeeSignOffMethodId,
        managerSignature,
        employeeSignature,
        signOffEmailAddress
    }
) {
    const methodKey = resolveEmployeeSignOffMethodKey(pendingReview, employeeSignOffMethodId);
    const trimmedSignOffEmailAddress = (signOffEmailAddress ?? "").trim();
    const topics = (pendingReview?.topics ?? []).map((topic) => {
        if (Object.hasOwn(topicComments, topic.topicKey)) {
            return { topicKey: topic.topicKey, comment: topicComments[topic.topicKey] ?? null, reviewed: true };
        }

        return { topicKey: topic.topicKey, comment: null, reviewed: false };
    });

    return {
        topics,
        outcomeLevelNumber: outcomeLevelNumber ?? null,
        outcomeSummary: outcomeSummary ?? null,
        nextReviewDate: toApiDate(customNextReviewDate),
        employeeSignOffMethodId: employeeSignOffMethodId ?? null,
        managerSignature: managerSignature ?? null,
        // Each of these belongs to one sign-off method alone, so it is sent as null for every
        // other one: an address must never be held against a review that will not be emailed,
        // nor a signature against a review that is not signed on screen.
        employeeSignature: methodKey === onScreenSignOffMethodKey ? (employeeSignature ?? null) : null,
        signOffEmailAddress: methodKey === emailSignOffMethodKey && trimmedSignOffEmailAddress !== "" ? trimmedSignOffEmailAddress : null
    };
}
